// src/pages/hr/learning/MyLearningTab.jsx
// ============================================================
// Learning & Development - My Learning
// Redesigned with Green Theme & Outline Icons
// ============================================================

import React, { useState } from "react";
import {
  Play,
  CheckCircle,
  Lock,
  Video,
  ArrowRight,
  Eye,
  Clock,
  Building,
  CheckCircle2,
} from "lucide-react";
import { getYouTubeEmbedUrl } from "../../../hooks/useLearning";

export default function MyLearningTab({
  learningState,
  currentUser,
  onNavigateTab,
}) {
  const { data, markVideoCompleted } = learningState;
  const courses = data?.courses || [];
  const progressRecords = data?.progressRecords || [];

  const [activeVideoId, setActiveVideoId] = useState(null);

  // Get the current employee's progress for a course
  const getProgress = (courseId) => {
    const empId = currentUser?.uid || currentUser?.id || "";
    return progressRecords.find((p) => p.courseId === courseId && p.employeeId === empId);
  };

  // Seed display courses if catalog is empty
  const displayCourses = courses.length > 0 ? courses : [
    {
      id: "seed-crs-1",
      title: "Cybersecurity & Data Privacy Essentials 2026",
      description: "Mandatory corporate data protection, phishing prevention, and secure coding practices.",
      category: "Compliance & Security",
      department: "All Departments",
      duration: "30 Mins",
      videoUrl: "https://www.youtube.com/watch?v=inWWhr5tnEA",
      embedUrl: "https://www.youtube.com/embed/inWWhr5tnEA",
      status: "Active",
      quiz: { passPercentage: 70, questions: [] },
    },
    {
      id: "seed-crs-2",
      title: "Microservices & Cloud Architecture Design",
      description: "Advanced guide to building scalable, resilient cloud applications.",
      category: "Technical Skills",
      department: "Engineering",
      duration: "60 Mins",
      videoUrl: "https://www.youtube.com/watch?v=rv4LlmLmvWk",
      embedUrl: "https://www.youtube.com/embed/rv4LlmLmvWk",
      status: "Active",
      quiz: { passPercentage: 75, questions: [] },
    },
  ];

  const handleMarkVideoComplete = (course) => {
    markVideoCompleted(course.id, currentUser, currentUser);
  };

  const completedCount = progressRecords.filter((p) => p.videoCompleted && (p.employeeId === (currentUser?.uid || currentUser?.id))).length;
  const pendingCount = displayCourses.length - completedCount;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .lnd-green-badge {
          background: #f0fdf4;
          color: #15803d;
          border: 1.5px solid #bbf7d0;
          padding: 0.2rem 0.6rem;
          border-radius: 99px;
          font-size: 0.72rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }
        .btn-green-primary {
          background: #1b4b31;
          color: #ffffff;
          border: none;
          border-radius: 9px;
          padding: 0.45rem 0.9rem;
          font-weight: 700;
          font-size: 0.8rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          transition: all 0.18s ease;
        }
        .btn-green-primary:hover {
          background: #143724;
        }
        .btn-outline-action {
          background: #ffffff;
          border: 1.5px solid #cbd5e1;
          border-radius: 9px;
          padding: 0.45rem 0.85rem;
          font-weight: 700;
          font-size: 0.78rem;
          color: #475569;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          transition: all 0.18s ease;
        }
        .btn-outline-action:hover {
          background: #f8fafc;
          border-color: #94a3b8;
        }
      `}</style>

      {/* Header Card */}
      <div className="card" style={{ padding: "1.1rem 1.35rem", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#ffffff", borderRadius: 14, border: "1px solid #e2e8f0" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 800, display: "flex", alignItems: "center", gap: "0.55rem", color: "#0f172a" }}>
            <Video size={18} strokeWidth={1.8} color="#15803d" /> My Assigned Training Courses
          </h3>
          <p style={{ margin: "0.25rem 0 0 0", fontSize: "0.8rem", color: "#64748b" }}>
            Watch the video training, then complete the quiz evaluation for certificate readiness.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.6rem" }}>
          <span className="lnd-green-badge">
            <CheckCircle2 size={12} strokeWidth={2} /> {completedCount} Completed
          </span>
          <span className="lnd-green-badge" style={{ background: "#f8fafc", color: "#475569", borderColor: "#e2e8f0" }}>
            <Clock size={12} strokeWidth={2} /> {pendingCount} Pending
          </span>
        </div>
      </div>

      {/* Course Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))", gap: "1.25rem" }}>
        {displayCourses.map((course) => {
          const progress = getProgress(course.id);
          const videoComplete = progress?.videoCompleted || false;
          const quizAttempted = progress?.quizAttempted || false;
          const isExpanded = activeVideoId === course.id;

          return (
            <div
              key={course.id}
              className="card"
              style={{
                padding: "1.35rem",
                background: "#ffffff",
                borderRadius: 14,
                border: "1px solid #e2e8f0",
                borderTop: videoComplete ? "4px solid #15803d" : "4px solid #10b981",
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
              }}
            >
              {/* Course Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.75rem" }}>
                <div>
                  <span className="lnd-green-badge" style={{ marginBottom: "0.35rem", display: "inline-block" }}>
                    {course.category}
                  </span>
                  <h4 style={{ margin: "0.25rem 0 0 0", fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>
                    {course.title}
                  </h4>
                </div>
                <div className="lnd-green-badge">
                  {quizAttempted ? "Completed" : videoComplete ? "Video Finished" : "In Progress"}
                </div>
              </div>

              <p style={{ margin: "0 0 0.75rem 0", fontSize: "0.82rem", color: "#64748b", lineHeight: 1.45 }}>
                {course.description}
              </p>

              {/* Embedded YouTube Video Player */}
              {isExpanded && (
                <div style={{ width: "100%", height: "220px", borderRadius: "10px", overflow: "hidden", marginBottom: "0.85rem", background: "#000" }}>
                  <iframe
                    width="100%"
                    height="100%"
                    src={course.embedUrl || getYouTubeEmbedUrl(course.videoUrl)}
                    title={course.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}

              {/* Meta Info */}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.76rem", color: "#475569", padding: "0.45rem 0.75rem", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px", marginBottom: "0.75rem" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                  <Clock size={13} strokeWidth={1.8} /> {course.duration}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                  <Building size={13} strokeWidth={1.8} /> {course.department}
                </span>
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                {!isExpanded ? (
                  <button
                    className="btn-green-primary"
                    onClick={() => setActiveVideoId(course.id)}
                  >
                    <Play size={14} strokeWidth={2} /> Watch Video
                  </button>
                ) : (
                  <button
                    className="btn-outline-action"
                    onClick={() => setActiveVideoId(null)}
                  >
                    <Eye size={14} strokeWidth={1.8} /> Collapse
                  </button>
                )}

                {!videoComplete && (
                  <button
                    className="btn-green-primary"
                    onClick={() => handleMarkVideoComplete(course)}
                  >
                    <CheckCircle size={14} strokeWidth={2} /> Mark Completed
                  </button>
                )}

                {videoComplete && !quizAttempted && (
                  <button
                    className="btn-green-primary"
                    onClick={() => onNavigateTab && onNavigateTab("quiz")}
                  >
                    <ArrowRight size={14} strokeWidth={2} /> Take Quiz Now
                  </button>
                )}

                {!videoComplete && (
                  <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "0.74rem", color: "#94a3b8", fontWeight: 500 }}>
                    <Lock size={12} strokeWidth={1.8} /> Quiz locked until video is watched
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
